import React from 'react';
import { PreferencesWithProfile } from './useProfilesWithPreferences';
import { APITypes, NotificationTypes } from '../../../../../primary-record-types';
import { getIntl } from '../../../../../utils/intl';
import { ConfigurableTable, ConfigurableTableRow } from '../../../../v2BaseComponents/ConfigurationTable';
import getName from '../../../../../utils/getName';

const intl = getIntl('ProfileGroupConfiguration', {
    'medical-profile-message': 'Profile Message',
    'medical-profile-activity-comment': 'Activity Comment',
    'medical-profile-note-comment': 'Note Comment',
    'collaborator-profiles': 'Collaborator Profiles',
    'your-profiles': 'Your Profiles',
    email: 'Email',
    push: 'Push Notifications',
});

export type ProfileGroupConfigurationOwnProps = {
    value: PreferencesWithProfile[];
    onChange: (value: PreferencesWithProfile[]) => void;
    isDisabled?: boolean;
    isCollaborators?: boolean;
};

export type ProfileGroupConfigurationProps = ProfileGroupConfigurationOwnProps;

const notificationTypes: NotificationTypes.MedicalProfileNotificationType[] = [
    'medical-profile-message',
    'medical-profile-activity-comment',
    'medical-profile-note-comment',
];

const deliveryTypes: NotificationTypes.DeliveryType[] = ['email', 'push'];

const ProfileGroupConfiguration: React.FC<ProfileGroupConfigurationProps> = (props) => {
    const forEveryNotificationType = <TResult,>(
        preferences: APITypes.userService.UserMedicalProfilePreferences['preferences'],
        callback: (deliverTypes: NotificationTypes.DeliveryType[]) => TResult,
    ) =>
        notificationTypes.map((notificationType) => {
            const deliverTypes = preferences[notificationType];
            return callback(deliverTypes);
        });
    const getProfileGroupDeliveryTypeValue = (
        profile: PreferencesWithProfile,
        deliverType: NotificationTypes.DeliveryType,
    ) => {
        const allSelected = forEveryNotificationType(profile.preferences, (deliverTypes) =>
            deliverTypes.includes(deliverType),
        ).every((_) => _);
        const noneSelected = forEveryNotificationType(
            profile.preferences,
            (deliverTypes) => !deliverTypes.includes(deliverType),
        ).every((_) => _);
        if (allSelected) {
            return true;
        } else if (noneSelected) {
            return false;
        } else {
            return 'indeterminate';
        }
    };

    const getProfileGroupDeliveryTypeToggle = (
        profile: PreferencesWithProfile,
        deliverType: NotificationTypes.DeliveryType,
    ) => {
        const currentValue = getProfileGroupDeliveryTypeValue(profile, deliverType);
        const newCheckboxValue = currentValue === 'indeterminate' ? false : !currentValue;

        const newProfile = {
            ...profile,
            preferences: {},
        } as PreferencesWithProfile;

        notificationTypes.forEach((notificationType) => {
            const deliverTypes = profile.preferences[notificationType].filter((_) => _ !== deliverType);
            if (newCheckboxValue) {
                deliverTypes.push(deliverType);
            }
            newProfile.preferences[notificationType] = deliverTypes;
        });

        const newValue = props.value.map((_) => (_.medicalProfileId === profile.medicalProfileId ? newProfile : _));
        props.onChange(newValue);
    };

    const handleDeliveryTypeToggle = (
        profile: PreferencesWithProfile,
        notificationType: NotificationTypes.MedicalProfileNotificationType,
        deliverType: NotificationTypes.DeliveryType,
        value: boolean,
    ) => {
        let existingDeliverTypes = [...profile.preferences[notificationType]];
        if (value) {
            existingDeliverTypes.push(deliverType);
        } else {
            existingDeliverTypes = existingDeliverTypes.filter((_) => _ !== deliverType);
        }

        const newProfile = {
            ...profile,
            preferences: {
                ...profile.preferences,
                [notificationType]: existingDeliverTypes,
            },
        };
        const newValue = props.value.map((_) => (_.medicalProfileId === profile.medicalProfileId ? newProfile : _));
        props.onChange(newValue);
    };

    if (!props.value.length) return null;

    return (
        <ConfigurableTable
            title={props.isCollaborators ? intl('collaborator-profiles') : intl('your-profiles')}
            checkboxTitles={deliveryTypes.map((deliveryType) => intl(deliveryType))}
            checkboxWidth={100}
            isDisabled={props.isDisabled}
        >
            {props.value.map((profile) => {
                const name = getName(profile.profile);
                return (
                    <React.Fragment key={profile.medicalProfileId}>
                        <ConfigurableTableRow
                            key={profile.medicalProfileId}
                            title={name}
                            isGroupRow
                            checkboxes={deliveryTypes.map((deliveryType) => ({
                                onToggle: () => getProfileGroupDeliveryTypeToggle(profile, deliveryType),
                                value: getProfileGroupDeliveryTypeValue(profile, deliveryType),
                                isDisabled: props.isDisabled,
                                testID: `delivery-group-checkbox-${name}-${deliveryType}`,
                            }))}
                        />
                        <>
                            {notificationTypes.map((notificationType, index) => (
                                <ConfigurableTableRow
                                    key={`${profile.medicalProfileId}-${notificationType}`}
                                    title={intl(notificationType)}
                                    checkboxes={deliveryTypes.map((deliveryType) => ({
                                        onToggle: (value) =>
                                            handleDeliveryTypeToggle(profile, notificationType, deliveryType, value),
                                        value: profile.preferences[notificationType].includes(deliveryType),
                                        isDisabled: props.isDisabled,
                                        testID: `delivery-type-checkbox-${name}-${notificationType}-${deliveryType}`,
                                    }))}
                                    isLastInGroupRow={index === notificationTypes.length - 1}
                                />
                            ))}
                        </>
                    </React.Fragment>
                );
            })}
        </ConfigurableTable>
    );
};

export default React.memo(ProfileGroupConfiguration);
